import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Divider, LinearProgress, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import { LockClock } from "@mui/icons-material";

type Result = {
  name: string;
  reg_number: string;
  score: string;
  started_at: string;
  submitted_at: string;
  payload: {
    id: string;
    answer: string;
    question: any
  }[][] | null;
  remarks: {
    overall_score: string;
    overall_total_marks: string;
    sections: any[]
  } | null;
}

const cleanPayload = (
  payload: Result["payload"]
): Result["payload"] => {
  if (!payload) return null;

  return payload
    .map(section =>
      section.filter(item => item.question?.type !== 0)
    )
};


export default function SubmissionDetails() {
    const { submission_id } = useParams<{ submission_id: string }>();
    const { getAssessmentResult } = useClassroomActions();

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<Result | null>(null);

    const [currentSectionIdx, setCurrentSectionIdx] = useState(0);

    
    const handleNextSection = () => {
      if (!result?.payload) return;
      if (currentSectionIdx < result.payload.length - 1) {
        setCurrentSectionIdx(currentSectionIdx + 1);
      }
    };

    const handlePrevSection = () => {
      if (currentSectionIdx > 0) {
        setCurrentSectionIdx(currentSectionIdx - 1);
      }
    };

    const fetchSubmission = async () => {
        setLoading(true);
        const _result = await getAssessmentResult(Number(submission_id));
        
        if (_result) {
          const cleanedResult: Result = {
            ..._result,
            payload: cleanPayload(_result.payload),
          };

          setResult(cleanedResult);
        }
        setLoading(false);
    }
    

    useEffect(() => {
        if (result) return;
        fetchSubmission();
    }, [submission_id]);
    

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!result) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Alert severity="error">Result not found</Alert>
            </Box>
        );
    }

  

    
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getScoreColor = (awarded: number, total: number) => {
        const percent = (awarded / total) * 100;
        if (percent >= 80) return 'success';
        if (percent >= 60) return 'primary';
        if (percent >= 40) return 'warning';
        return 'error';
    };


    return (
      <Box sx={{ bgcolor: '#c6e1f5', minHeight: '100vh',}}>
        <Container sx={{ paddingY: 2 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Submission Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <User size={18} color="#666" /> */}
                    <Typography variant="body2" color="text.secondary">
                      Student: <strong>{result.name}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <Award size={18} color="#666" /> */}
                    <Typography variant="body2" color="text.secondary">
                      Reg. Number: <strong>{result.reg_number}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
          
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main" gutterBottom>
                  {result.score}
                </Typography>
      
              </Box>

              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LockClock sx={{color: "#666", fontSize: 16 }} />
                    <Typography variant="caption" color="text.secondary">Started</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(result.started_at)}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LockClock sx={{color: "#666", fontSize: 16 }} />
                    <Typography variant="caption" color="text.secondary">Submitted</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(result.submitted_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {result.remarks ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    This submission has been automatically graded by AI
                </Alert>
            ) : (
               <Alert severity="warning" sx={{ mt: 2 }}>
                    This submission has not been graded by AI
                </Alert>
            )}

          </Paper>

        <Box>
        {result.payload?.[currentSectionIdx] && (() => {
          const section = result.payload[currentSectionIdx];
          const sectionRemarks = result.remarks?.sections?.[currentSectionIdx];
          
          return (
            <Paper elevation={1} sx={{ mb: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#f8f9fa', px: 3, py: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="600">
                    Section {currentSectionIdx + 1} of {result.payload.length}
                  </Typography>
                  {/* <Chip 
                    label={sectionRemarks
                      ? `${sectionRemarks.section_score}/${sectionRemarks.section_total_marks} marks`
                      : "Not graded"}
                    color={sectionRemarks ? getScoreColor(Number(sectionRemarks.section_score), Number(sectionRemarks.section_total_marks)) : 'default'}
                    size="small"
                  /> */}
                </Box>
              </Box>
              
              <Box sx={{ p: 3 }}>
                {section?.map((payload, idx) => {
                  const questionRemarks = sectionRemarks?.questions?.[idx];
                  return (
                    <Box key={idx} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ flex: 1 }}>
                          Question {idx + 1}: {payload?.question?.title ?? "Untitled Question"}
                        </Typography>
                        <Chip 
                          label={questionRemarks
                            ? `${questionRemarks.awarded_marks}/${questionRemarks.total_marks}`
                            : "N/A"}
                          size="small"
                          variant="outlined"
                          color={questionRemarks ? getScoreColor(Number(questionRemarks.awarded_marks), Number(questionRemarks.total_marks)) : 'default'}
                        />
                      </Box>
                      <Box sx={{ bgcolor: '#fafafa', p: 2, borderRadius: 1, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Student Answer:
                        </Typography>
                        <Typography variant="body2">
                          {payload?.answer || "No answer provided"}
                        </Typography>
                      </Box>
                      {questionRemarks?.reasoning && (
                        <Box sx={{ bgcolor: '#f0f7ff', p: 2, borderRadius: 1, borderLeft: '3px solid #2196f3' }}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            AI Feedback:
                          </Typography>
                          <Typography variant="body2">
                            {questionRemarks.reasoning}
                          </Typography>
                        </Box>
                      )}
                      {idx < section.length - 1 && <Divider sx={{ mt: 3 }} />}
                    </Box>
                  );
                })}
              </Box>
              
              {/* Navigation Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 2, 
                borderTop: '1px solid #e0e0e0',
                bgcolor: '#fafafa'
              }}>
                <Button
                  variant="outlined"
                  onClick={handlePrevSection}
                  disabled={currentSectionIdx === 0}
                  startIcon={<span>←</span>}
                >
                  Prev
                </Button>
                
                <Typography variant="body2" color="text.secondary">
                  Section {currentSectionIdx + 1} of {result.payload.length}
                </Typography>
                
                <Button
                  variant="outlined"
                  onClick={handleNextSection}
                  disabled={currentSectionIdx === result.payload.length - 1}
                  endIcon={<span>→</span>}
                >
                  Next
                </Button>
              </Box>
            </Paper>
          );
        })()}
      </Box>
      </Container>
      </Box>
    );
}